/* eslint-disable react-refresh/only-export-components -- 与工具函数同文件导出 */
import * as Popover from '@radix-ui/react-popover';
import { ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '@/components/ui/modal';
import { cn } from '@/lib/utils';

export type EstimatedDurationLegacyPreset = '0-15' | '15-30' | '30-45';
export type EstimatedDurationPreset = 15 | 30 | 45 | 'custom';
export type EstimatedDurationPresetValue = EstimatedDurationPreset | EstimatedDurationLegacyPreset;

export interface EstimatedDurationState {
  preset: EstimatedDurationPresetValue;
  /** 自定义：分 0~5 */
  customMinutes: number;
  /** 自定义：秒，取值受分约束 */
  customSeconds: number;
}

const MINUTE_OPTIONS = [0, 1, 2, 3, 4, 5] as const;
const ALL_SECOND_OPTIONS = [0, 15, 30, 45] as const;

// 默认自定义分钟数
const DEFAULT_CUSTOM_MINUTES = 0;
// 默认自定义秒数
const DEFAULT_CUSTOM_SECONDS = 15;

// 根据分钟数获取可选的秒数列表
export function getSecondOptions(minutes: number): number[] {
  if (minutes === 5) return [0];
  if (minutes === 0) return [15, 30, 45];
  return [...ALL_SECOND_OPTIONS];
}

// 约束自定义秒数，确保在分钟数对应的合法范围内
export function clampCustomSeconds(minutes: number, seconds: number): number {
  const allowed = getSecondOptions(minutes);
  if (allowed.includes(seconds)) return seconds;
  return allowed[0]!;
}

// 将预设值标准化为数字，支持 legacy 格式
function normalizePreset(preset: EstimatedDurationPresetValue): EstimatedDurationPreset {
  if (preset === '0-15') return 15;
  if (preset === '15-30') return 30;
  if (preset === '30-45') return 45;
  return preset;
}

// 获取预估时长总秒数
export function getEstimatedDurationSeconds(state: EstimatedDurationState): number {
  const normalizedPreset = normalizePreset(state.preset);
  if (normalizedPreset === 'custom') {
    return state.customMinutes * 60 + state.customSeconds;
  }
  return normalizedPreset;
}

/**
 * 由后端返回的总秒数构造选择器状态。
 * - 15 / 30 / 45 秒分别对应前三个预设；
 * - 其他值视为自定义，按 15 秒粒度对齐到 (0~5 分) × {0,15,30,45 秒} 网格，
 *   并约束到合法范围（自定义最小 0 分 15 秒，最大 5 分 0 秒）。
 */
export function createEstimatedDurationStateFromSeconds(
  seconds: number,
): EstimatedDurationState {
  const safe = Math.max(0, Math.round(Number(seconds) || 0));

  if (safe === 15) return { preset: 15, customMinutes: DEFAULT_CUSTOM_MINUTES, customSeconds: DEFAULT_CUSTOM_SECONDS };
  if (safe === 30) return { preset: 30, customMinutes: DEFAULT_CUSTOM_MINUTES, customSeconds: DEFAULT_CUSTOM_SECONDS };
  if (safe === 45) return { preset: 45, customMinutes: DEFAULT_CUSTOM_MINUTES, customSeconds: DEFAULT_CUSTOM_SECONDS };

  const MIN_CUSTOM_TOTAL = 15;
  const MAX_CUSTOM_TOTAL = 5 * 60;
  const clampedTotal = Math.min(MAX_CUSTOM_TOTAL, Math.max(MIN_CUSTOM_TOTAL, safe));
  const stepped = Math.round(clampedTotal / 15) * 15;

  let minutes = Math.floor(stepped / 60);
  let secs = stepped - minutes * 60;
  if (minutes > 5) {
    minutes = 5;
    secs = 0;
  }

  return {
    preset: 'custom',
    customMinutes: minutes,
    customSeconds: clampCustomSeconds(minutes, secs),
  };
}

/** 外层按钮右侧展示的时长文案 */
export function formatEstimatedDurationButtonLabel(state: EstimatedDurationState): string {
  const normalizedPreset = normalizePreset(state.preset);
  switch (normalizedPreset) {
    case 15:
      return '0~15';
    case 30:
      return '15~30';
    case 45:
      return '30~45';
    case 'custom': {
      const total = state.customMinutes * 60 + state.customSeconds;
      const left = Math.max(0, total - 15);
      return `${left}~${total}`;
    }
    default:
      return '—';
  }
}

type SegmentOpt = {
  value: EstimatedDurationPreset;
  label: React.ReactNode;
};

export const EstimatedVideoDuration: React.FC<{
  value: EstimatedDurationState;
  onChange: (next: EstimatedDurationState) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(false);
  const [draftMinutes, setDraftMinutes] = useState(value.customMinutes);
  const [draftSeconds, setDraftSeconds] = useState(value.customSeconds);

  const presetOpts: SegmentOpt[] = useMemo(
    () => [
      { value: 15, label: '0~15s' },
      { value: 30, label: '15~30s' },
      { value: 45, label: '30~45s' },
      { value: 'custom', label: t('ai_creation.duration_custom') },
    ],
    [t],
  );

  useEffect(() => {
    if (!customOpen) return;
    // 如果当前外部状态本来就是自定义，就把 draftMinutes/draftSeconds 同步成当前保存值（回显用户之前选的自定义时长）。
    if (value.preset === 'custom') {
      setDraftMinutes(value.customMinutes);
      setDraftSeconds(value.customSeconds);
    } else {
      // 如果当前不是自定义，打开弹窗时给一个默认值（0 分 15 秒）。
      setDraftMinutes(DEFAULT_CUSTOM_MINUTES);
      setDraftSeconds(DEFAULT_CUSTOM_SECONDS);
    }
  }, [customOpen, value.preset, value.customMinutes, value.customSeconds]);

  useEffect(() => {
    setDraftSeconds((prev) => {
      const allowed = getSecondOptions(draftMinutes);
      return allowed.includes(prev) ? prev : allowed[0]!;
    });
  }, [draftMinutes]);

  const handlePresetClick = (p: EstimatedDurationPreset) => {
    if (p === 'custom') return;
    setCustomOpen(false);
    onChange({ ...value, preset: p });
  };

  const handleConfirmCustom = () => {
    const sec = clampCustomSeconds(draftMinutes, draftSeconds);
    onChange({
      preset: 'custom',
      customMinutes: draftMinutes,
      customSeconds: sec,
    });
    setCustomOpen(false);
  };

  const customSegmentActive = normalizePreset(value.preset) === 'custom' || customOpen;

  const minuteSelectOptions = MINUTE_OPTIONS.map((n) => ({
    label: String(n),
    value: String(n),
  }));

  const secondSelectOptions = getSecondOptions(draftMinutes).map((n) => ({
    label: String(n),
    value: String(n),
  }));

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="px-0.5 text-xs font-medium text-[rgba(152,162,179,1)]">
        {t('ai_creation.estimated_video_duration')}
      </p>

      <Popover.Root modal={false} open={customOpen} onOpenChange={setCustomOpen}>
        <div className="relative w-full">
          <div className="rounded-[8px] bg-[rgba(16,24,40,0.05)] p-[2px]">
            <div className="grid grid-cols-4 gap-0">
              {presetOpts.map((opt) =>
                opt.value === 'custom' ? (
                  <Popover.Trigger key={opt.value} asChild>
                    <button
                      type="button"
                      className={cn(
                        'flex h-[36px] cursor-pointer select-none items-center justify-center rounded-[6px] transition-all',
                        'flex-row gap-1 whitespace-nowrap text-sm',
                        customSegmentActive
                          ? 'bg-white font-medium text-[rgba(16,24,40,1)] shadow-sm'
                          : 'text-[rgba(102,112,133,1)] hover:bg-[rgba(16,24,40,0.02)]',
                      )}
                    >
                      {normalizePreset(value.preset) === 'custom'
                        ? `${formatEstimatedDurationButtonLabel(value)}s`
                        : t('ai_creation.duration_custom')}
                      <ChevronRight className="size-4 flex-shrink-0 text-[rgba(152,162,179,1)]" />
                    </button>
                  </Popover.Trigger>
                ) : (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePresetClick(opt.value)}
                    className={cn(
                      'flex h-[36px] cursor-pointer select-none items-center justify-center rounded-[6px] transition-all',
                      'flex-row gap-1.5 text-sm',
                      !customOpen && normalizePreset(value.preset) === opt.value
                        ? 'bg-white font-medium text-[rgba(16,24,40,1)] shadow-sm'
                        : 'text-[rgba(102,112,133,1)] hover:bg-[rgba(16,24,40,0.02)]',
                    )}
                  >
                    {opt.label}
                  </button>
                ),
              )}
            </div>
          </div>

          <Popover.Anchor asChild>
            <div
              className="pointer-events-none absolute bottom-0 left-full top-0 w-px shrink-0"
              aria-hidden
            />
          </Popover.Anchor>

          <Popover.Portal>
            <Popover.Content
              side="right"
              align="start"
              sideOffset={20}
              alignOffset={-50}
              onOpenAutoFocus={(e) => e.preventDefault()}
              className={cn(
                'z-[75] w-[min(272px,calc(100vw-24px))] rounded-[12px] border border-[rgba(234,236,240,1)] bg-white p-4',
                '[box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-[rgba(16,24,40,1)]">
                  {t('ai_creation.duration_approximate')}
                </span>
                <button
                  type="button"
                  onClick={handleConfirmCustom}
                  className="text-sm font-semibold text-[rgba(16,24,40,1)] hover:opacity-80"
                >
                  {t('ai_creation.duration_confirm')}
                </button>
              </div>
              <div className="flex flex-nowrap items-center justify-between gap-2">
                <div className="flex shrink-0 items-center gap-1.5">
                  <Dropdown
                    value={String(draftMinutes)}
                    onValueChange={(v) => setDraftMinutes(Number(v))}
                    options={minuteSelectOptions}
                    width="90px"
                    showDefaultOption={false}
                    contentClassName="z-[90]"
                    className={cn(
                      'h-10 rounded-[8px] border border-[rgba(208,213,221,1)] bg-white px-2 font-medium text-[rgba(16,24,40,1)] shadow-none',
                      'hover:bg-[rgba(16,24,40,0.03)] hover:border-[rgba(208,213,221,1)]',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(16,24,40,0.12)]',
                    )}
                  />
                  <span className="shrink-0 text-sm text-[rgba(16,24,40,1)]">
                    {t('ai_creation.duration_minute_unit')}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Dropdown
                    value={String(draftSeconds)}
                    onValueChange={(v) => setDraftSeconds(Number(v))}
                    options={secondSelectOptions}
                    width="90px"
                    showDefaultOption={false}
                    contentClassName="z-[90]"
                    className={cn(
                      'h-10 rounded-[8px] border border-[rgba(208,213,221,1)] bg-white px-2 font-medium text-[rgba(16,24,40,1)] shadow-none',
                      'hover:bg-[rgba(16,24,40,0.03)] hover:border-[rgba(208,213,221,1)]',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(16,24,40,0.12)]',
                    )}
                  />
                  <span className="shrink-0 text-sm text-[rgba(16,24,40,1)]">
                    {t('ai_creation.duration_second_unit')}
                  </span>
                </div>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </div>
      </Popover.Root>
    </div>
  );
};
